import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../components/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, LabelList } from 'recharts';
import { Building2, Users, CheckCircle2, AlertCircle, Briefcase, ShieldCheck, BarChart3, Percent, Thermometer, Building, Box, ClipboardCheck } from 'lucide-react';

const StateByRegion = () => {
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  // Récupérer la liste des régions
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await api.get('/collect/regions/');
        setRegions(response.data);
      } catch (error) {
        console.error('Erreur lors de la récupération des régions:', error);
      }
    };
    fetchRegions();
  }, []);

  // Récupérer les statistiques quand une région est sélectionnée
  useEffect(() => {
    const fetchStats = async () => {
      if (!selectedRegion) return;
      
      setLoading(true);
      try {
        const response = await api.get(`/collect/stats-region/${selectedRegion}/`);
        setStats(response.data);
      } catch (error) {
        console.error('Erreur lors de la récupération des statistiques:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [selectedRegion]);

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Sélecteur de région */}
          <div className="mb-8">
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="w-full md:w-64 px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Sélectionner une région</option>
              {regions.map((region) => (
                <option key={region.id} value={region.id}>
                  {region.nom}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
          ) : stats ? (
            <div className="space-y-8">
              {/* Première ligne - Statistiques principales */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  title="Total des dépôts"
                  count={stats.total_depots}
                  icon={Building2}
                  bgColor="bg-gradient-to-br from-violet-500 to-violet-700"
                  subtitle="Dans la région"
                />
                <StatCard
                  title="Dépôts reconnus"
                  count={stats.depots_reconnus}
                  percentage={((stats.depots_reconnus / stats.total_depots) * 100).toFixed(1)}
                  icon={CheckCircle2}
                  bgColor="bg-gradient-to-br from-emerald-500 to-emerald-700"
                  subtitle={`Sur ${stats.total_depots} dépôts`}
                />
                <StatCard
                  title="Employés"
                  count={stats.ressources_humaines.total_employes}
                  icon={Users}
                  bgColor="bg-gradient-to-br from-blue-500 to-blue-700"
                  subtitle={`${stats.ressources_humaines.moyenne_employes} en moyenne par dépôt`}
                />
                <StatCard
                  title="Avec Supervision"
                  count={stats.conformite.supervision.total}
                  percentage={stats.conformite.supervision.pourcentage}
                  icon={ShieldCheck}
                  bgColor="bg-gradient-to-br from-orange-500 to-orange-700"
                  subtitle={`Sur ${stats.total_depots} dépôts`}
                />
              </div>

              {/* Deuxième ligne - Statistiques RH et Infrastructure */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  title="Employés Permanents"
                  count={stats.ressources_humaines.permanents.total}
                  percentage={stats.ressources_humaines.permanents.pourcentage}
                  icon={Briefcase}
                  bgColor="bg-gradient-to-br from-pink-500 to-pink-700"
                  subtitle={`Sur ${stats.ressources_humaines.total_employes} employés`}
                />
                <StatCard
                  title="Port d'insignes"
                  count={stats.ressources_humaines.port_insignes.total}
                  percentage={stats.ressources_humaines.port_insignes.pourcentage}
                  icon={Users}
                  bgColor="bg-gradient-to-br from-cyan-500 to-cyan-700"
                  subtitle={`Sur ${stats.total_depots} dépôts`}
                />
                <StatCard
                  title="Avec Climatisation"
                  count={stats.infrastructure.climatisation.total}
                  percentage={stats.infrastructure.climatisation.pourcentage}
                  icon={Thermometer}
                  bgColor="bg-gradient-to-br from-teal-500 to-teal-700"
                  subtitle={`Sur ${stats.total_depots} dépôts`}
                />
                <StatCard
                  title="En Location"
                  count={stats.infrastructure.location.total}
                  percentage={stats.infrastructure.location.pourcentage}
                  icon={Building}
                  bgColor="bg-gradient-to-br from-indigo-500 to-indigo-700"
                  subtitle={`Sur ${stats.total_depots} dépôts`}
                />
              </div>

              {/* Troisième ligne - Statistiques supplémentaires */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  title="Avec Réfrigérateur"
                  count={stats.infrastructure.refrigerateur.total}
                  percentage={stats.infrastructure.refrigerateur.pourcentage}
                  icon={Box}
                  bgColor="bg-gradient-to-br from-amber-500 to-amber-700"
                  subtitle={`Sur ${stats.total_depots} dépôts`}
                />
                <StatCard
                  title="Connaissance Arrêté"
                  count={stats.conformite.connaissance_arrete.total}
                  percentage={stats.conformite.connaissance_arrete.pourcentage}
                  icon={ClipboardCheck}
                  bgColor="bg-gradient-to-br from-rose-500 to-rose-700"
                  subtitle={`Sur ${stats.total_depots} dépôts`}
                />
              </div>

              {/* Graphique des départements - Reconnus vs Non Reconnus */}
              <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-violet-500" />
                  Répartition par département (Reconnus vs Non Reconnus)
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.depots_par_departement}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-50" />
                      <XAxis dataKey="nom" />
                      <YAxis />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          borderRadius: '0.5rem',
                          border: 'none',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="reconnus" 
                        name="Reconnus" 
                        stroke="#10B981" 
                        strokeWidth={3}
                        dot={{ fill: '#10B981', strokeWidth: 2 }}
                        activeDot={{ r: 6 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="non_reconnus" 
                        name="Non Reconnus" 
                        stroke="#F97316" 
                        strokeWidth={3}
                        dot={{ fill: '#F97316', strokeWidth: 2 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              
              {/* Nouveau graphique - Nombre total de dépôts par département */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2 text-indigo-500" />
                  Nombre total de dépôts par département
                </h3>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.depots_par_departement}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-50" />
                      <XAxis dataKey="nom" />
                      <YAxis />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          borderRadius: '0.5rem',
                          border: 'none',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                        }}
                        formatter={(value) => [`${value} dépôts`, 'Total']}
                      />
                      <Legend />
                      <Bar 
                        dataKey="total" 
                        name="Nombre total de dépôts" 
                        fill="#6366F1" 
                        barSize={40}
                      >
                        <LabelList 
                          dataKey="total" 
                          position="top" 
                          formatter={(value) => value}
                          style={{ fill: '#4F46E5', fontWeight: 'bold' }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500">
              Sélectionnez une région pour voir les statistiques
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

// Composant StatCard avec le design original
const StatCard = ({ title, count, percentage, icon: Icon, bgColor, subtitle }) => (
  <div className={`${bgColor} rounded-xl shadow-sm p-6 text-white h-32`}>
    <div className="flex items-start justify-between">
      <div>
        <h3 className="font-medium opacity-90 text-sm">{title}</h3>
        <p className="text-2xl font-bold mt-1">
          {count?.toLocaleString()}
          {percentage !== undefined && ` (${percentage}%)`}
        </p>
        {subtitle && (
          <p className="text-xs mt-1 opacity-80">{subtitle}</p>
        )}
      </div>
      <div className="bg-white/20 p-2 rounded-xl">
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </div>
);

export default StateByRegion;
